import React, { useState, useRef, useEffect } from "react";
import { Slide } from "react-slideshow-image";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { groupdetails } from "./funtionsBD/Mainservice";
import GroupMembersOverviewDialog from "./GroupMembersOverviewDialog";
import { isTourDemoId } from "../tour/tourDemoFixtures";
import { usePermissions } from "../context/Permissioncontext";
import { userCanManageTrainingSessions } from "../utils/planningAccess";
import { isPlayerAccount, readActiveHomeRole } from "../utils/homeRole";

import "react-slideshow-image/dist/styles.css";
import "./Slider.css";
import "./Metric.css";

const SLIDES_DESKTOP = 3;
const SLIDES_MOBILE = 1;
const MOBILE_MQ = "(max-width: 640px)";

function useGroupsSlidesToShow() {
  const [slidesToShow, setSlidesToShow] = useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) return SLIDES_DESKTOP;
    return window.matchMedia(MOBILE_MQ).matches ? SLIDES_MOBILE : SLIDES_DESKTOP;
  });

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mq = window.matchMedia(MOBILE_MQ);
    const update = () => {
      setSlidesToShow(mq.matches ? SLIDES_MOBILE : SLIDES_DESKTOP);
    };
    update();
    if (typeof mq.addEventListener === "function") {
      mq.addEventListener("change", update);
      return () => mq.removeEventListener("change", update);
    }
    mq.addListener(update);
    return () => mq.removeListener(update);
  }, []);

  return slidesToShow;
}

const Slider = ({
  images,
  selectedgrp,
  setselectedgrp,
  availableGroups = [],
  onPlayerNewSession = null,
}) => {
  const slidesToShow = useGroupsSlidesToShow();
  const imageRefs = useRef([]);
  const [groupMembersDialogOpen, setGroupMembersDialogOpen] = useState(false);
  const [selectedGroupForMembers, setSelectedGroupForMembers] = useState(null);
  const [groupMembers, setGroupMembers] = useState({
    players: [],
    coaches: [],
    objectiveSummary: { primary: [], secondary: [] },
  });
  const [loadingMembers, setLoadingMembers] = useState(false);
  const { t } = useTranslation();
  const { hasPermission, permissions } = usePermissions();
  const user = JSON.parse(localStorage.getItem("user") || "null");
  const homeRole = readActiveHomeRole(user);
  const canManageSessions =
    !isPlayerAccount(user, { homeRole }) &&
    userCanManageTrainingSessions(user, permissions, hasPermission);
  const showPlayerNewSession = typeof onPlayerNewSession === "function";

  const groups = Array.isArray(images) ? images : [];

  const closeGroupMembersDialog = () => {
    setGroupMembersDialogOpen(false);
    setSelectedGroupForMembers(null);
    setGroupMembers({
      players: [],
      coaches: [],
      objectiveSummary: { primary: [], secondary: [] },
    });
  };

  const fetchGroupMembers = async (gr_id) => {
    if (!gr_id) return;
    setLoadingMembers(true);
    try {
      const response = await groupdetails(gr_id);
      const groupData = response.data.data || {};
      const summary = groupData.groupObjectiveSummary || {
        primary: [],
        secondary: [],
      };
      setGroupMembers({
        players: groupData.tbl_players || [],
        coaches: groupData.tbl_coaches || [],
        objectiveSummary: {
          primary: Array.isArray(summary.primary) ? summary.primary : [],
          secondary: Array.isArray(summary.secondary) ? summary.secondary : [],
        },
      });
    } catch (error) {
      console.error("Error fetching group members:", error);
      setGroupMembers({
        players: [],
        coaches: [],
        objectiveSummary: { primary: [], secondary: [] },
      });
    } finally {
      setLoadingMembers(false);
    }
  };

  const openGroupOverview = (slideImage) => {
    if (!slideImage?.groupDetails?.gr_id) return;
    setSelectedGroupForMembers(slideImage);
    setGroupMembersDialogOpen(true);
    fetchGroupMembers(slideImage.groupDetails.gr_id);
  };

  const handleGroupClick = (slideImage) => {
    setselectedgrp(slideImage);
    const id = slideImage?.groupDetails?.gr_id || slideImage?.gr_id;
    if (isTourDemoId(id)) return;
    localStorage.setItem("grpinf", JSON.stringify(slideImage));
    openGroupOverview(slideImage);
  };

  const handleAddSession = (slideImage, event) => {
    event?.stopPropagation?.();
    event?.preventDefault?.();
    setselectedgrp(slideImage);
    const id = slideImage?.groupDetails?.gr_id || slideImage?.gr_id;
    if (!isTourDemoId(id)) {
      localStorage.setItem("grpinf", JSON.stringify(slideImage));
    }
    window.dispatchEvent(
      new CustomEvent("openAddSessionDialog", {
        detail: {
          selectedgrp: slideImage,
          availableGroups: Array.isArray(availableGroups) && availableGroups.length
            ? availableGroups
            : groups,
        },
        bubbles: true,
        cancelable: true,
      })
    );
  };

  const handlePlayerNewSession = (slideImage, event) => {
    event?.stopPropagation?.();
    event?.preventDefault?.();
    setselectedgrp?.(slideImage);
    const id = slideImage?.groupDetails?.gr_id || slideImage?.gr_id;
    if (id && !isTourDemoId(id)) {
      localStorage.setItem("grpinf", JSON.stringify(slideImage));
    }
    onPlayerNewSession?.(slideImage);
  };

  const defaultIndex = (() => {
    const selectedId = selectedgrp?.groupDetails?.gr_id;
    if (!selectedId) return 0;
    const idx = groups.findIndex(
      (g) => String(g?.groupDetails?.gr_id || "") === String(selectedId)
    );
    return idx >= 0 ? idx : 0;
  })();

  if (!groups.length) return null;

  const canSlide = groups.length > slidesToShow;

  return (
    <>
      <div className="metrics-container groups-slider-container">
        <Slide
          key={`${slidesToShow}-${groups.length}`}
          slidesToScroll={1}
          slidesToShow={Math.min(slidesToShow, groups.length)}
          indicators={canSlide}
          arrows={false}
          autoplay={false}
          infinite={canSlide}
          canSwipe={canSlide}
          defaultIndex={Math.min(defaultIndex, Math.max(0, groups.length - 1))}
          cssClass="groups-slideshow"
        >
          {groups.map((slideImage, index) => {
            const gid = slideImage?.groupDetails?.gr_id;
            const name = slideImage?.groupDetails?.gr_name || "";
            const initial = (name || "?").slice(0, 1);
            const isSelected =
              String(selectedgrp?.groupDetails?.gr_id || "") === String(gid || "");
            const players = Number(slideImage?.totalPlayers) || 0;
            const sessions = Number(slideImage?.totalSessions) || 0;
            const remaining = Number(slideImage?.remainingSessions) || 0;
            const showCoachAdd = canManageSessions;
            const showPlayerAdd = showPlayerNewSession;
            return (
              <div className="slide groups-slide" key={gid || index}>
                <div
                  className={
                    isSelected
                      ? "Groupcard groupcard selectedgrp groupcard--selected fade"
                      : "Groupcard groupcard fade"
                  }
                  onClick={() => handleGroupClick(slideImage)}
                  role="button"
                  tabIndex={0}
                  aria-pressed={isSelected}
                  aria-label={
                    name
                      ? `${name}. ${players} ${t("totalplayer")}, ${remaining} ${t("remainingsession")}`
                      : t("traininggrp")
                  }
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleGroupClick(slideImage);
                    }
                  }}
                >
                  <div className="ImageOverlay groupcard__media">
                    {slideImage?.groupDetails?.gr_image == null ? (
                      <div className="group_dummyimg groupcard__monogram" aria-hidden>
                        {initial}
                      </div>
                    ) : (
                      <BlurredImage
                        src={slideImage?.groupDetails?.gr_image}
                        alt=""
                        ref={(el) => {
                          imageRefs.current[index] = el;
                        }}
                      />
                    )}
                    <span className="groupcard__plaque" title={t("remainingsession")}>
                      <span className="groupcard__plaque-num">{remaining}</span>
                      <span className="groupcard__plaque-lbl">
                        {t("remainingsession")}
                      </span>
                    </span>
                  </div>
                  <div className="group_name groupcard__body">
                    <p>{name || "—"}</p>
                    <div className="groupcard__lanes">
                      <div className="groupcard__lane">
                        <span className="groupcard__lane-val">{players}</span>
                        <span className="groupcard__lane-lbl">{t("totalplayer")}</span>
                      </div>
                      <div className="groupcard__lane">
                        <span className="groupcard__lane-val">{sessions}</span>
                        <span className="groupcard__lane-lbl">{t("totalsession")}</span>
                      </div>
                    </div>
                  </div>
                  {showCoachAdd ? (
                    <button
                      type="button"
                      className="groupcard__add-session"
                      data-tour-id={index === 0 ? "coach-add-session" : undefined}
                      aria-label={t("addsession")}
                      title={t("addsession")}
                      onClick={(e) => handleAddSession(slideImage, e)}
                    >
                      <FontAwesomeIcon icon={faPlus} />
                      <span className="groupcard__add-session-label">
                        {t("addsession")}
                      </span>
                    </button>
                  ) : null}
                  {showPlayerAdd ? (
                    <button
                      type="button"
                      className="groupcard__add-session groupcard__add-session--player"
                      data-tour-id={index === 0 ? "player-new-session" : undefined}
                      aria-label={t("playerNewTrainingSession")}
                      title={t("playerNewTrainingSession")}
                      onClick={(e) => handlePlayerNewSession(slideImage, e)}
                    >
                      <FontAwesomeIcon icon={faPlus} />
                      <span className="groupcard__add-session-label">
                        {t("playerNewTrainingSession")}
                      </span>
                    </button>
                  ) : null}
                </div>
              </div>
            );
          })}
        </Slide>
      </div>

      <GroupMembersOverviewDialog
        open={groupMembersDialogOpen}
        onClose={closeGroupMembersDialog}
        group={selectedGroupForMembers}
        members={groupMembers}
        loading={loadingMembers}
        availableGroups={
          Array.isArray(availableGroups) && availableGroups.length
            ? availableGroups
            : groups
        }
      />
    </>
  );
};

export default Slider;

const BlurredImage = React.forwardRef(({ src, alt }, ref) => {
  const [loaded, setLoaded] = useState(false);

  return (
    <>
      <img
        ref={ref}
        src={src}
        alt={alt}
        className={`image ${loaded ? "loaded" : "loading"}`}
        onLoad={() => setLoaded(true)}
      />
      {!loaded && <div className="blur-placeholder"></div>}
    </>
  );
});
